import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FormInput } from "./custom/components";
import db, { IndexDBProduct } from "../db/db";
import { decodeHtml } from "../config/functions";

interface Dropdown {
  label: string;
  value: string;
}

type DropdownPlacement =
  | { mode: "below"; top: number; left: number; width: number; maxHeight: number }
  | {
      mode: "above";
      bottom: number;
      left: number;
      width: number;
      maxHeight: number;
    };

interface ProductSearchProps {
  handleProductName: (
    product: IndexDBProduct | null,
    id: number,
    field: string,
    value: string,
  ) => void;
  pi: { id: number; productId: number; productName: Dropdown | null };
  resetSearch: boolean;
  setResetSearch: React.Dispatch<React.SetStateAction<boolean>>;
  editProductName: Dropdown | null;
  handleKeyDown?: (
    e: React.KeyboardEvent | React.MouseEvent,
    nextField: string,
    rowId: string,
    moveToNextRow?: boolean,
  ) => void;
  refs?: React.MutableRefObject<{
    [key: string]: any;
  }>;
}

const DROPDOWN_Z = 10560;
const EDGE = 8;

const ProductSearch: React.FC<ProductSearchProps> = ({
  handleProductName,
  pi,
  resetSearch,
  setResetSearch,
  editProductName,
  handleKeyDown,
  refs,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>(
    editProductName?.label || "",
  );
  const [filteredProducts, setFilteredProducts] = useState<IndexDBProduct[]>([]);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);
  const [, setIsDropdownFocused] = useState<boolean>(false);
  const [dropdownPlacement, setDropdownPlacement] =
    useState<DropdownPlacement | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRootRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = useCallback(() => {
    const input = inputRef.current;
    if (!input || !showTable || filteredProducts.length === 0) {
      setDropdownPlacement(null);
      return;
    }

    const rect = input.getBoundingClientRect();
    const margin = 6;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const spaceBelow = viewportH - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const preferredMax = Math.min(380, viewportH * 0.48);
    const minUseful = 96;

    const placeBelow =
      spaceBelow >= minUseful ||
      (spaceBelow >= spaceAbove && spaceBelow >= 72);

    const maxHBelow = Math.max(140, Math.min(preferredMax, spaceBelow));
    const maxHAbove = Math.max(140, Math.min(preferredMax, spaceAbove));

    // Width scales with viewport (caps at full usable width, min matches input)
    const targetWidth = Math.min(
      viewportW - 2 * EDGE,
      Math.max(rect.width, Math.round(viewportW * 0.92)),
    );
    let left = rect.left;
    const maxLeft = viewportW - targetWidth - EDGE;
    if (left > maxLeft) left = Math.max(EDGE, maxLeft);
    if (left < EDGE) left = EDGE;

    if (placeBelow) {
      setDropdownPlacement({
        mode: "below",
        top: rect.bottom + margin,
        left,
        width: targetWidth,
        maxHeight: maxHBelow,
      });
    } else {
      setDropdownPlacement({
        mode: "above",
        bottom: viewportH - rect.top + margin,
        left,
        width: targetWidth,
        maxHeight: maxHAbove,
      });
    }
  }, [showTable, filteredProducts.length]);

  useLayoutEffect(() => {
    updateDropdownPosition();
  }, [updateDropdownPosition, filteredProducts, selectedRowIndex]);

  useEffect(() => {
    if (!showTable || filteredProducts.length === 0) return;

    updateDropdownPosition();

    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);

    const el = inputRef.current;
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => updateDropdownPosition());
      ro.observe(el);
    }

    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      ro?.disconnect();
    };
  }, [showTable, filteredProducts.length, updateDropdownPosition]);

  useEffect(() => {
    if (editProductName) {
      setSearchTerm(editProductName.label);
    } else {
      setSearchTerm("");
    }
  }, [editProductName]);

  useEffect(() => {
    if (resetSearch) {
      setSearchTerm("");
      setFilteredProducts([]);
      setShowTable(false);
      setSelectedRowIndex(-1);
      setIsDropdownFocused(false);
      setDropdownPlacement(null);
      setResetSearch(false);
    }
  }, [resetSearch, setResetSearch]);

  useEffect(() => {
    setSelectedRowIndex(-1);
  }, [filteredProducts]);

  const fetchProducts = async (value:any) => {
    if (value.trim()) {
      try {
        const response = await db.products.toArray();
        const filteredDb = response.filter(
          (d) =>
            (d.productName?.toLowerCase().includes(value.toLowerCase()) ??
              false) ||
            (d.modelNumber
              ?.toString()
              .toLowerCase()
              .includes(value.toLowerCase()) ??
              false) ||
            (d.category?.toLowerCase().includes(value.toLowerCase()) ??
              false) ||
            (d.brand?.toLowerCase().includes(value.toLowerCase()) ?? false),
        );

        const filtered: IndexDBProduct[] = filteredDb.map((d) => ({
          id: d.id as number,
          productId: d.productId,
          productName: d.productName ?? "",
          productCode: d.productCode ?? "",
          modelNumber: d.modelNumber ?? "",
          batchNumber: d.batchNumber ?? 0,
          sac: d.hsnCode ?? "",
          gst: d.gst ?? 0,
          // productDetails: d.productDetails ?? "",
          pwoGst: d.pwoGst ?? 0,
          pwGst: d.pwGst ?? 0,
          wholesaleRate: d.wholesaleRate ?? 0,
          retailRate: d.retailRate ?? 0,
          mrp: d.mrp ?? 0,
          stock: d.stock ?? 0,
          department: d.department ?? "",
          category: d.category ?? "",
          brand: d.brand ?? "",
          // vendor: d.vendor ?? "",
          updatedByOn:
            d.updatedByOn !== undefined && d.updatedByOn !== null
              ? String(d.updatedByOn)
              : "",
          hsnCode: d.hsnCode ?? "",
        }));

        setFilteredProducts(filtered);
        setShowTable(filtered.length > 0);
        setSelectedRowIndex(filtered.length > 0 ? 0 : -1);
      } catch (error) {
        console.error("Error fetching products:", error);
        setFilteredProducts([]);
        setShowTable(false);
      }
    } else {
      setFilteredProducts([]);
      setShowTable(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchProducts(value);
    setSelectedRowIndex(-1);
  };

  const handleSelectProduct = (
    product: IndexDBProduct,
    e?: React.KeyboardEvent | React.MouseEvent,
  ) => {
    const productLabel = `${product.productName}`;
    setSearchTerm(productLabel);
    setShowTable(false);
    setSelectedRowIndex(-1);
    setDropdownPlacement(null);

    handleProductName(product, pi.id, "qty", pi.id.toString());

    if (handleKeyDown && e) {
      handleKeyDown(e, "qty", pi.id.toString());
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<Element>) => {
    if (filteredProducts.length === 0 || !showTable) {
      if (handleKeyDown && (e.key === "Enter" || e.key === "Tab")) {
        e.preventDefault();
        handleKeyDown(e, "qty", pi.id.toString());
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setShowTable(true);
        setSelectedRowIndex((prev) =>
          prev < filteredProducts.length - 1 ? prev + 1 : 0,
        );
        break;

      case "ArrowUp":
        e.preventDefault();
        setShowTable(true);
        setSelectedRowIndex((prev) =>
          prev > 0 ? prev - 1 : filteredProducts.length - 1,
        );
        break;

      case "Enter":
        e.preventDefault();
        if (selectedRowIndex >= 0 && filteredProducts[selectedRowIndex]) {
          handleSelectProduct(filteredProducts[selectedRowIndex], e);
        } else if (handleKeyDown) {
          handleKeyDown(e, "qty", pi.id.toString());
        }
        break;

      case "Escape":
        e.preventDefault();
        setShowTable(false);
        setSelectedRowIndex(-1);
        setDropdownPlacement(null);
        break;

      default:
        break;
    }
  };

  const handleRowKeyDown = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    index: number,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelectProduct(filteredProducts[index], e);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedRowIndex((prev) =>
        prev < filteredProducts.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedRowIndex((prev) =>
        prev > 0 ? prev - 1 : filteredProducts.length - 1,
      );
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowTable(false);
      setSelectedRowIndex(-1);
      setDropdownPlacement(null);
      setIsDropdownFocused(false);
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (showTable && selectedRowIndex >= 0 && tableRef.current) {
      const row = tableRef.current.querySelector(
        `tr[data-row-index="${selectedRowIndex}"]`,
      );
      row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedRowIndex, showTable]);

  useEffect(() => {
    const handleGlobalMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        showTable &&
        dropdownRootRef.current &&
        !dropdownRootRef.current.contains(t) &&
        inputRef.current &&
        !inputRef.current.contains(t)
      ) {
        setShowTable(false);
        setSelectedRowIndex(-1);
        setIsDropdownFocused(false);
        setDropdownPlacement(null);
      }
    };

    document.addEventListener("mousedown", handleGlobalMouseDown);
    return () => {
      document.removeEventListener("mousedown", handleGlobalMouseDown);
    };
  }, [showTable]);

  const dropdownPortal =
    showTable &&
    filteredProducts.length > 0 &&
    dropdownPlacement &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            ref={dropdownRootRef}
            className="product-search-dropdown rounded border border-gray-300 bg-white"
            style={{
              position: "fixed",
              zIndex: DROPDOWN_Z,
              left: dropdownPlacement.left,
              width: dropdownPlacement.width,
              maxHeight: dropdownPlacement.maxHeight,
              overflow: "auto",
              boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
              ...(dropdownPlacement.mode === "below"
                ? { top: dropdownPlacement.top }
                : { bottom: dropdownPlacement.bottom }),
            }}
          >
            <table
              ref={tableRef}
              className="w-full border-collapse text-xs"
              style={{ minWidth: "100%" }}
            >
              <thead className="sticky top-0 z-[1] bg-primary text-white">
                <tr className="text-[10px]">
                  <th className="border border-white border-opacity-25 px-1 py-1 w-24">
                    Product Name
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Part No
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Batch No
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-20">
                    SAC/HSN Code
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-12">GST</th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Price w/o GST
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Price w/ GST
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-20">
                    Wholesale Rate
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-20">
                    Retail Rate
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-12">MRP</th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-12">
                    Stock
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Department
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Category
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-16">
                    Brand
                  </th>
                  <th className="border border-white border-opacity-25 px-1 py-1 w-24">
                    Updated On
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white text-[10px]">
                {filteredProducts.map((product, index) => (
                  <tr
                    key={`${product.id}-${index}`}
                    data-row-index={index}
                    style={{
                      backgroundColor:
                        selectedRowIndex === index ? "#3dffa8ff" : "white",
                    }}
                    onClick={(e) => handleSelectProduct(product, e)}
                    onKeyDown={(e) => handleRowKeyDown(e, index)}
                    tabIndex={0}
                  >
                    <td className="border border-gray-300 px-1 py-1">
                      {decodeHtml(product.productName || '')}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.modelNumber}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.batchNumber}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.hsnCode}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.gst}%
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.pwoGst}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.pwGst}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.wholesaleRate}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.retailRate}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.mrp}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.stock}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.department}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.category}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.brand}
                    </td>
                    <td className="border border-gray-300 px-1 py-1">
                      {product.updatedByOn}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative" style={{ width: "250px" }}>
      <FormInput
        name={`productName-${pi.id}`}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onFocus={() => setShowTable(true)}
        className="border rounded w-full p-2"
        placeholder="Search product..."
        style={{ height: "7px" }}
        ref={(el) => {
          if (el) {
            (
              inputRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = el;
            if (refs) {
              refs.current[`productName-${pi.id}`] = el;
            }
          } else {
            (
              inputRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = null;
            if (refs) {
              refs.current[`productName-${pi.id}`] = null;
            }
          }
        }}
      />
      {dropdownPortal}
    </div>
  );
};

export default ProductSearch;
